import { useState } from 'react';
import { useRouter } from 'next/router';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
// Hooks
import { useAuth } from '@/hooks/useAuth';
import { useLogin } from '@/hooks/useLogin';
// Utils
import { brand } from '@/brand/brand';
import { routes } from '@/routes/routes';
import { getHasValidEmail } from '@/utils/validation';
import { config } from '../../config';
// Components
import { AlertBody, LoadingSpinner } from '@/common/index';
// Styles
import arrow from '@/icons/fontawesome/arrow-right-solid.svg';
import styles from './forgot-password.module.css';
// Types
import { ErrorState } from './types';

const initialState = {
  value: '',
  error: false,
  message: '',
};

const initialErrorState: ErrorState = {
  error: false,
  header: '',
  message: null,
  body: '',
};

export default function ForgotPassword() {
  const router = useRouter();
  const { sendForgotPasswordEmail } = useAuth();
  const { errorMessages, handleLinkClick } = useLogin();
  const [email, setEmail] = useState(initialState);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<ErrorState>(initialErrorState);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = ({ target: { value } }) => {
    setEmail((prevState) => ({ ...prevState, value: value }));
  };

  const handleValidationOnBlur = ({ target: { value } }) => {
    if (value === '') return;
    const validEmail = getHasValidEmail({ email: value });
    setEmail((prevState) => ({
      ...prevState,
      error: !validEmail,
      message: validEmail ? '' : 'Enter a valid email (sally@example.com)',
    }));
  };

  const clearValidationErrorOnFocus = () => {
    setEmail((prevState) => ({
      ...prevState,
      error: false,
      message: '',
    }));
    setError(initialErrorState);
  };

  const handleSendForgotEmail = (e) => {
    e.preventDefault();
    setIsLoading(true);
    localStorage.setItem('email', email.value);
    return sendForgotPasswordEmail({
      email: email.value,
      link: `${window.location.origin}/reset-password`,
    })
      .then(() => {
        setShowSuccess(true);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err?.error?.ErrorDetail?.includes('Member with FirebaseUid:  not found.')) {
          setShowSuccess(true);
          setIsLoading(false);
        } else {
          setError(errorMessages.generic);
        }
        setIsLoading(false);
      });
  };

  const handleRedirect = () => {
    handleLinkClick(routes.login.path);
  };

  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        background: `url(${config.brand.loginImage})`,
        overflow: 'auto',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        '@media (max-width: 500px)': {
          alignItems: 'flex-start',
          paddingTop: '40px',
        },
      }}
    >
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '488px',
          height: 'fit-content',
          gap: '16px',
          '@media (max-width: 500px)': {
            width: 'calc(100% - 40px)',
          },
        }}
      >
        <form className={styles.form} onSubmit={handleSendForgotEmail}>
          <div className={styles.logoContainer}>
            <span>
              <a
                data-core-qa="forgotPasswordHomeIcon"
                onClick={() => router.push(brand.loggedOutHomeRoute)}
                className={styles.logo}
              >
                <img
                  src={brand.authenticationLogo}
                  alt={`${brand.brandName} Logo`}
                  width="auto"
                  height="50"
                />
              </a>
            </span>
          </div>
          <div className={styles.header}>
            <Typography variant="h2" data-core-qa="forgotPasswordTitleText">
              {showSuccess ? 'Reset Link Sent' : 'Reset Your Password'}
            </Typography>
          </div>
          {showSuccess ? (
            <div className={styles.success}>
              <Typography
                variant="body2"
                className={styles.subHeader}
                data-core-qa="forgotPasswordTitleSubText"
              >
                If an account associated with this email address exists, we will send you
                a password reset email.
              </Typography>
              <Typography
                variant="body2"
                className={styles.subHeader}
                data-core-qa="forgotPasswordTitleSubTextTwo"
              >
                If the email doesn&apos;t show up soon, check your spam folder or try
                again.
              </Typography>
            </div>
          ) : (
            <Typography
              variant="body2"
              className={styles.subHeader}
              data-core-qa="forgotPasswordTitleSubText"
            >
              No worries, we&apos;ll send you new instructions to reset your password.
            </Typography>
          )}
          {error.error ? (
            <div className={styles.errorMessageContainer}>
              <Alert severity="error" variant="outlined">
                <AlertBody header={error.header} body={error.body} children={null} />
              </Alert>
            </div>
          ) : null}
          {!showSuccess ? (
            <div className={styles.input}>
              <TextField
                id="forgotPasswordInputEmail"
                type="text"
                size="small"
                fullWidth
                error={email.error}
                helperText={email.message}
                label="Email Address"
                onChange={handleInputChange}
                value={email.value}
                onBlur={handleValidationOnBlur}
                onFocus={clearValidationErrorOnFocus}
                data-core-qa="forgotPasswordInputEmail"
                placeholder="email@example.com"
              />
            </div>
          ) : null}

          {!showSuccess ? (
            <Button
              variant="contained"
              size="large"
              fullWidth
              type="submit"
              data-core-qa="forgotPasswordButtonSubmit"
              disabled={error.error || email.error || email.value === ''}
            >
              {isLoading ? (
                <LoadingSpinner />
              ) : showSuccess ? (
                'Return to Login'
              ) : (
                'Request reset link'
              )}
            </Button>
          ) : null}
        </form>
        <Button
          variant="outlined"
          type="button"
          className={styles.cancelButton}
          data-core-qa="forgotPasswordButtonCancel"
          onClick={handleRedirect}
          fullWidth
        >
          Return to Login
          <img src={arrow.src} alt="arrow" className={styles.arrowIcon} />
        </Button>
      </Paper>
    </Box>
  );
}

ForgotPassword.strictlyPublicRoute = true;
